/**
 * Copyright (c) 2020
 *
 * Contains the instance for http calls to customersManager API
 *
 * @summary instance of axios for customersManager calls
 * @author Julien <julien.donnadieu@yooda.com>
 *
 * Created at     : 2020-06-08 10:21:56
 * Last modified  : 2020-06-25 16:34:05
 */

import Vue from 'vue'
import axios from 'axios'
import authService from '@/service/auth/AuthService.js'
import ImpersonateService from '@/service/impersonate/ImpersonateService'
import { v4 as uuidv4 } from 'uuid';
// import axiosRetry from 'axios-retry';

const axiosForOneBackend = axios.create({
    baseURL: `${process.env.VUE_APP_YOODA_ONE_BACKEND_BASE_URI}`,
    withCredentials: true,
})

const getReportingToken = () => {
    return new URLSearchParams(window.location.search).get('hash')
}

const isReportingMode = () => {
    return getReportingToken() !== null
}

const logout = async () => {
    try {
        Vue.$log.info('logout', 'in')

        await authService.redirectToExternalLogoutPage(window.location.href)

    } catch (error) {
        Vue.$log.error('logout', error)
    } finally {
        Vue.$log.info('logout', 'out')
    }
}

axiosForOneBackend.interceptors.request.use((config) => {

    if(isReportingMode()) {
        config.headers["reporting-token"] = getReportingToken()
    }

    if (ImpersonateService.isCustomerSwitched) {
        config.params = config.params || {}
        config.params['_switch_user'] = ImpersonateService.emailToSwitch
    } else {
        config.params = config.params || {}
    }

    // add uuid to identify api call when retry
    if (!config.hasOwnProperty('uuid')) {
        config.uuid = uuidv4()
    }

    return config
});

// declare an array of uuids (string) to be used to stop process when consecutive 401 append
let unauthorizedUuids = [];

axiosForOneBackend.interceptors.response.use(
    async (response) => {

        const {config, status} = response

        Vue.$log.info('axiosForOneBackend.interceptor.response.onSuccess', {
            'request.method': config.method,
            'request.url': config.url,
            'request.params': config.params,
            'request.data': config.data,
            'response.status': status,
            'response.data': response ? response.data : null,
            'authService.isAuthenticated': authService.isAuthenticated(),
        })

        if (status !== 204 && response.data.content === undefined) {
            Vue.$log.error('response must be a YoodaResponse')
        }

        // remove all non-unauthorized uuids to be used to stop process when consecutive 401 append
        if (status !== 401 && config.hasOwnProperty('uuid')) {
            unauthorizedUuids = unauthorizedUuids.filter(function(uuid) {
                return uuid !== config.uuid;
            });
        }

        return response.data
    },
    async (error) => {

        const {config, response} = error

        Vue.$log.info('axiosForOneBackend.interceptor.response.onError', {
            'request.method': config.method,
            'request.url': config.url,
            'request.params': config.params,
            'request.data': config.data,
            'response.status': response ? response.status : 'unknown',
            'response.data': response ? response.data : null,
            'authService.isAuthenticated': authService.isAuthenticated(),
        })

        // save all unauthorized uuids to be used to stop process when consecutive 401 append
        if (response && response.status === 401 && config.hasOwnProperty('uuid')) {
            unauthorizedUuids.push(config.uuid);
        }

        if (config.url === '/customers-manager/token/refresh') {
            Vue.$log.debug('axiosForCustomersManager.interceptor.response.onError', 'url is refreshToken => logout')
            await logout(error)
            return new Promise((resolve, reject) => {
                reject(error)
            })
        } else if (response === null || response === undefined) {
            Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'response is undefined => logout')
            return new Promise((resolve, reject) => {
                reject(error)
            })
        } else if (response.status !== 401) {
            Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'response is not 401, reject and continue')
            return new Promise((resolve, reject) => {
                reject(error)
            })
        } else if (!authService.isAuthenticated()) {
            Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'response is 401 and user is not authenticated')
            if (isReportingMode()) {
                Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'viewing mode enabled, continue')
            } else {
                Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'viewing mode disabled, logout')
                await logout(error)
            }
            return new Promise((resolve, reject) => {
                reject(error)
            })
        } else { // else refresh token
            Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'response is 401, user authenticated')

            // tempo to avoid massive calls to server
            await new Promise(resolve => setTimeout(resolve, 1000))

            if (config.hasOwnProperty('uuid') && unauthorizedUuids.filter(uuid => uuid === config.uuid).length > 1) {
                Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'not the first time it appends consecutively => logout')
                await logout(error)
                return new Promise((resolve, reject) => {
                    reject(error)
                })
            } else if (!isReportingMode()) {
                Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'viewing mode is enabled => refresh token attempt')
                await authService.refreshToken()
                return axiosForOneBackend.request(config)
            } else {
                Vue.$log.debug('axiosForOneBackend.interceptor.response.onError', 'viewing mode is disabled => logout')
                return new Promise((resolve, reject) => {
                    reject(error)
                })
            }
        }
    }
);

export default axiosForOneBackend
